import { Box, Stack, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { GoalsFulfillmentProvider } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentContext";
import { GoalsFulfillmentTerm } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentTerm";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { useLockedInvestmentCaseStatus } from "Components/Analyses/_Shared/useLockedInvestmentCaseStatus";
import { useGetAnalysisGoals, useListContractProposals, useListCurrentContracts } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  ContractProposalBaseSolutionHorizonType,
  type ContractProposalListItem,
  CurrentContractBaseSolutionHorizonType,
  type CurrentContractListItem,
  GoalOverviewHorizon,
  NsurePlanOverviewHorizon,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useParams } from "react-router-dom";

const SOLUTION_TO_NSURE_HORIZON: Record<ContractProposalBaseSolutionHorizonType, NsurePlanOverviewHorizon> = {
  [ContractProposalBaseSolutionHorizonType.short_term]: NsurePlanOverviewHorizon.short,
  [ContractProposalBaseSolutionHorizonType.mid_term]: NsurePlanOverviewHorizon.middle,
  [ContractProposalBaseSolutionHorizonType.long_term]: NsurePlanOverviewHorizon.long,
};

export const PdfTemplateGoalFulfillment = () => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const {
    data: goalsData,
    isFetched: isGoalsFetched,
    isError: isGoalsError,
  } = useGetAnalysisGoals(analysisId, versionId, {
    lifecycleStatus: "active",
    progressStatus: "in_progress",
  });

  const {
    data: contractProposals,
    isFetched: isContractProposalsFetched,
    isError: isContractProposalsError,
  } = useListContractProposals(analysisId, versionId);
  const {
    data: currentContracts,
    isFetched: isCurrentContractsFetched,
    isError: isCurrentContractsError,
  } = useListCurrentContracts(analysisId, versionId);
  const { lockedCaseStatus, isInvestmentCaseLocked, isInvestmentStatesFetched, isInvestmentStatesError } = useLockedInvestmentCaseStatus(
    analysisId,
    versionId,
  );

  const goals = goalsData?.goals ?? [];

  const getNsurePlanByHorizon = (horizon: ContractProposalBaseSolutionHorizonType) =>
    goalsData?.nsurePlans?.find((plan) => plan.horizon === SOLUTION_TO_NSURE_HORIZON[horizon]);

  const getContractProposalsByHorizon = (horizon: ContractProposalBaseSolutionHorizonType): ContractProposalListItem[] => {
    if (!contractProposals) return [];
    return contractProposals.items.filter((item) => item.horizonType === horizon);
  };

  const getCurrentContractsByHorizon = (horizon: CurrentContractBaseSolutionHorizonType): CurrentContractListItem[] => {
    if (!currentContracts) return [];
    return currentContracts.items.filter((item) => item.horizonType === horizon);
  };

  const getFutureAmountByHorizon = (horizon: ContractProposalBaseSolutionHorizonType): number => {
    switch (horizon) {
      case ContractProposalBaseSolutionHorizonType.short_term:
        return (
          (contractProposals?.summary.shortTerm.futureAmount.value ?? 0) + (currentContracts?.summary.shortTerm.futureAmount.value ?? 0)
        );
      case ContractProposalBaseSolutionHorizonType.mid_term:
        return (contractProposals?.summary.midTerm.futureAmount.value ?? 0) + (currentContracts?.summary.midTerm.futureAmount.value ?? 0);
      case ContractProposalBaseSolutionHorizonType.long_term:
        return (contractProposals?.summary.longTerm.futureAmount.value ?? 0) + (currentContracts?.summary.longTerm.futureAmount.value ?? 0);
      default:
        return 0;
    }
  };

  if (isGoalsError || isContractProposalsError || isCurrentContractsError || isInvestmentStatesError) {
    return (
      <Stack sx={{ flex: 4 }} spacing={Spacing.S}>
        <Typography variant="h6">{t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}</Typography>
        <Typography variant="body2">{t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}</Typography>
      </Stack>
    );
  }

  if (!isGoalsFetched || !isContractProposalsFetched || !isCurrentContractsFetched || !isInvestmentStatesFetched) return null;

  return (
    <AnalysisDetailContext.Provider value={{ analysisId, versionId, readOnly: true, canAddFamilyMember: false }}>
      <InvestmentCaseContext.Provider value={{ lockedCaseStatus, isInvestmentCaseLocked }}>
        <GoalsFulfillmentProvider clientGuid={undefined}>
          <Stack sx={{ flex: 4 }} spacing={Spacing.XXL}>
            <Typography variant="h6">Plnění cílů</Typography>

            <Box sx={{ pr: Spacing.XXL }}>
              <GoalsFulfillmentTerm
                horizonType={ContractProposalBaseSolutionHorizonType.short_term}
                goals={goals.filter((item) => item.horizon === GoalOverviewHorizon.short)}
                contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
                currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.short_term)}
                futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
                handleCurrentSolutionDrawerOpen={() => {}}
                handleProposedSolutionDrawerOpen={() => {}}
                nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
              />

              <GoalsFulfillmentTerm
                horizonType={ContractProposalBaseSolutionHorizonType.mid_term}
                goals={goals.filter((item) => item.horizon === GoalOverviewHorizon.middle)}
                contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
                currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.mid_term)}
                futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
                handleCurrentSolutionDrawerOpen={() => {}}
                handleProposedSolutionDrawerOpen={() => {}}
                nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
              />

              <GoalsFulfillmentTerm
                horizonType={ContractProposalBaseSolutionHorizonType.long_term}
                goals={goals.filter((item) => item.horizon === GoalOverviewHorizon.long)}
                contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
                currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.long_term)}
                futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
                handleCurrentSolutionDrawerOpen={() => {}}
                handleProposedSolutionDrawerOpen={() => {}}
                nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
              />
            </Box>
          </Stack>
        </GoalsFulfillmentProvider>
      </InvestmentCaseContext.Provider>
    </AnalysisDetailContext.Provider>
  );
};
